const _excluded = ["className", "stickyToTop", "items", "size", "bgColor", "testId"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], t.indexOf(o) >= 0 || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.indexOf(n) >= 0) continue; t[n] = r[n]; } return t; }
import React, { useMemo } from 'react';
import cn from 'classnames';
import { WideArrowDownward } from "../../icons/WideArrowDownward";
import { WideArrowUpward } from "../../icons/WideArrowUpward";
import { Checkbox } from "../Checkbox";
import { useDevice } from "../Device";
import { IconButton } from "../IconButton";
import { Tooltip } from "../Tooltip";
import styles from "./TableList.module.scss";
import { actionColumnDef, expandIconStyle, useTableListContext } from "./TableList.utils";
import { TableListHeaderItem } from "./TableListHeaderItem";
import { TableListItem } from "./TableListItem";
import { TableListSortIcons } from "./TableListSortIcons";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const defaultSortOrder = ['desc', 'asc'];
function getNextSort(sort, sortOrder) {
  if (!sort) {
    return sortOrder[0];
  }
  const idx = sortOrder.findIndex(so => so === sort.sort);
  return sortOrder[(idx + 1) % sortOrder.length];
}
const TableHeaderItem = _ref => {
  let {
    item,
    idx,
    columnDef,
    sort,
    setSort,
    testId
  } = _ref;
  if (!columnDef) return null;
  const isSortable = columnDef.sortable;
  const isSorted = sort && idx === sort.colIndex;
  return /*#__PURE__*/_jsx(TableListHeaderItem, {
    columnDef: columnDef,
    item: item,
    testId: testId && "".concat(testId, "-").concat(idx),
    children: isSortable && /*#__PURE__*/_jsxs("button", {
      className: cn(styles.sortable, {
        [styles.sortActive]: isSorted
      }),
      "data-testid": testId && "".concat(testId, "-").concat(idx, "-sort-").concat(isSorted && sort ? sort.sort : 'none'),
      onClick: () => {
        const nextSort = getNextSort(isSorted ? sort : undefined, columnDef.sortOrder || defaultSortOrder);
        setSort(nextSort && {
          colIndex: idx,
          sort: nextSort
        });
      },
      children: [item.value, /*#__PURE__*/_jsx(TableListSortIcons, {
        sort: isSorted ? sort.sort : undefined
      })]
    })
  }, idx);
};
export function TableListHeader(props) {
  const {
    isMobileDevice
  } = useDevice();
  const {
      className,
      stickyToTop,
      items,
      size = 'medium',
      bgColor = 'gray',
      testId
    } = props,
    rest = _objectWithoutProperties(props, _excluded);
  const {
    columnDefs,
    expandableRows,
    expandableRowsVariant = 'default',
    enableRowSelection,
    sort,
    hasActionColumn,
    numRows,
    numSelectedRows,
    hPadding,
    onToggleAllSelection,
    setSort,
    expandAllRows,
    expandedRows,
    justifyBetween
  } = useTableListContext();
  const isExpandableDefaultVariant = expandableRows && expandableRowsVariant === 'default';
  const isExpandableClickableIconAndRowVariant = expandableRows && expandableRowsVariant === 'clickableIconAndRow';
  const isExpandableClickableIconVariant = expandableRows && (expandableRowsVariant === 'clickableIcon' || isExpandableClickableIconAndRowVariant);
  const hasExpendedRow = !!Object.values(expandedRows).filter(row => row).length;
  const stickyItems = useMemo(() => items.filter((_, idx) => {
    var _columnDefs$idx;
    return (_columnDefs$idx = columnDefs[idx]) === null || _columnDefs$idx === void 0 ? void 0 : _columnDefs$idx.sticky;
  }), [items, columnDefs]);
  const stickyColumnDefs = useMemo(() => columnDefs.filter(col => col.sticky), [columnDefs]);
  const nonStickyItems = useMemo(() => items.filter((_, idx) => {
    var _columnDefs$idx2;
    return !((_columnDefs$idx2 = columnDefs[idx]) !== null && _columnDefs$idx2 !== void 0 && _columnDefs$idx2.sticky);
  }), [items, columnDefs]);
  const nonStickyColumnDefs = useMemo(() => columnDefs.filter(col => !col.sticky), [columnDefs]);
  if (isMobileDevice) {
    return null;
  }
  return /*#__PURE__*/_jsxs("div", _objectSpread(_objectSpread({}, rest), {}, {
    className: cn(styles.header, styles["".concat(bgColor, "Bg")], styles["".concat(hPadding, "Padding")], styles["".concat(size, "Size")], stickyToTop && styles.stickyToTop, justifyBetween && styles.justifyBetween, className),
    "data-testid": testId,
    children: [(enableRowSelection || isExpandableClickableIconVariant || stickyItems.length > 0) && /*#__PURE__*/_jsxs("div", {
      className: cn(styles.stickyCheckboxWrapper, styles["".concat(hPadding, "Padding")]),
      children: [(enableRowSelection || isExpandableClickableIconVariant) && /*#__PURE__*/_jsxs("div", {
        className: cn(styles.stickyCheckbox, styles["".concat(hPadding, "Padding")]),
        children: [enableRowSelection && /*#__PURE__*/_jsx(Checkbox, {
          testId: testId && "".concat(testId, "-checkbox"),
          checked: numRows != null && numRows === numSelectedRows,
          partiallyChecked: numSelectedRows != null && numSelectedRows > 0 && numSelectedRows !== numRows,
          onChange: e => {
            e.stopPropagation();
            if (onToggleAllSelection != null) {
              onToggleAllSelection(e.target.checked);
            }
          }
        }), isExpandableClickableIconVariant && /*#__PURE__*/_jsx(Tooltip, {
          placement: "right",
          title: hasExpendedRow ? 'Collapse all sub items' : 'Expand all sub items',
          children: /*#__PURE__*/_jsx(IconButton, {
            testId: testId && "".concat(testId, "-toggle-expand-all"),
            size: "small",
            noPadding: true,
            onClick: e => {
              e.stopPropagation();
              if (expandAllRows != null) {
                const expandedRowsValues = Object.values(expandedRows);
                const shouldExpandAllRows = expandedRowsValues.filter(row => !row).length === expandedRowsValues.length;
                expandAllRows(shouldExpandAllRows);
              }
            },
            children: hasExpendedRow ? /*#__PURE__*/_jsx(WideArrowUpward, {
              height: 20,
              width: 20
            }) : /*#__PURE__*/_jsx(WideArrowDownward, {
              height: 20,
              width: 20
            })
          })
        })]
      }), stickyItems.length > 0 && /*#__PURE__*/_jsx("div", {
        className: cn(styles.headerItemData, justifyBetween && styles.justifyBetween),
        children: stickyItems.map((item, idx) => {
          var _item$testId;
          return /*#__PURE__*/_jsx(TableHeaderItem, {
            item: item,
            idx: idx,
            columnDef: stickyColumnDefs[idx],
            sort: sort,
            setSort: setSort,
            testId: (_item$testId = item.testId) !== null && _item$testId !== void 0 ? _item$testId : testId
          }, idx);
        })
      })]
    }), /*#__PURE__*/_jsx("div", {
      className: cn(styles.headerItemData, justifyBetween && styles.justifyBetween),
      children: nonStickyItems.map((item, idx) => /*#__PURE__*/_jsx(TableHeaderItem, {
        item: item,
        idx: idx + stickyItems.length,
        columnDef: nonStickyColumnDefs[idx],
        sort: sort,
        setSort: setSort,
        testId: testId
      }, idx + stickyItems.length))
    }), (hasActionColumn || isExpandableDefaultVariant) && /*#__PURE__*/_jsxs("div", {
      className: cn(styles.rowActionSticky, styles["".concat(hPadding, "Padding")]),
      children: [hasActionColumn && /*#__PURE__*/_jsx(TableListItem, {
        columnDef: actionColumnDef,
        item: {
          value: ''
        },
        testId: testId && "".concat(testId, "-action-column")
      }), isExpandableDefaultVariant && /*#__PURE__*/_jsx("div", {
        style: expandIconStyle
      })]
    })]
  }));
}